/* @ngInject */

export default (
  $rootScope,
  helperService,
  expensesService,
  helperPdfService,
  householdsService,
  HouseholdGroup
) => {
  /**
   * Prepare expenses by household.
   *
   * @param {Array} householdExpenses
   * @param {Array} householdGroupExpenses
   *
   * @returns {Array}
   */
  const prepareExpensesByHousehold = ({ householdExpenses, householdGroupExpenses }) => {
    const content = [];
    const len = householdGroupExpenses?.length || 0;
    for (let h = 0; h < len; h += 1) {
      const householdGroupExpense = householdGroupExpenses[h];
      const eLen = householdGroupExpense?.group?.length || 0;
      if (eLen) {
        content.push(
          helperPdfService.createSubHeader({
            title: householdGroupExpense?.name?.toUpperCase()
          })
        );
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: []
          },
          layout: 'noBorders'
        });
        let bodyContent = 0;
        for (let e = 0; e < eLen; e += 1) {
          const householdExpense = householdGroupExpense?.group[e];
          if (helperService.getOrganizationCountry().cca2 !== 'NZ') {
            if (e === 0 || e === 4 || e === 8 || e === 12 || e === 16) {
              content[content.length - 1].table.body.push(['', '', '', '']);
              bodyContent = 0;
            }
            content[content.length - 1].table.body[
              content[content.length - 1].table.body.length - 1
            ][bodyContent] = {
              text: [
                {
                  text: `${householdExpense?.type?.title}\n`,
                  style: 'label'
                },
                {
                  text: [
                    {
                      text: householdExpense?.value.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        2
                      ),
                      style: 'value'
                    },
                    {
                      text: ` (${householdExpense?.frequency.title})`,
                      style: 'valueXS'
                    }
                  ]
                }
              ]
            };
            bodyContent += 1;
          } else {
            content[content.length - 1].table.body.push([
              {
                text: [
                  {
                    text: `${householdExpense?.type?.titleNZ}\n`,
                    style: 'label'
                  },
                  {
                    text: householdExpense?.value.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      2
                    ),
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  {
                    text: 'Frequency\n',
                    style: 'label'
                  },
                  {
                    text: householdExpense?.frequency.title,
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  {
                    text: 'Comments\n',
                    style: 'label'
                  },
                  {
                    text: householdExpense?.comments || '-',
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              ''
            ]);
          }
        }
        content[content.length - 1].table.body.push(['', '', '', '']);
        content[content.length - 1].table.body[
          content[content.length - 1].table.body.length - 1
        ][0] = {
          text: [
            {
              text: `Total monthly ${householdGroupExpense?.name?.toLowerCase()}\n`,
              style: 'label'
            },
            {
              text: householdsService
                .getTotalOrGroupTotalExpensesByHousehold({
                  householdExpenses: householdGroupExpense?.group
                })
                .formatWithCurrency($rootScope.CurrentCurrency(), 2),
              style: 'value'
            }
          ],
          colSpan: 2
        };
      }
    }

    content.push(helperPdfService.createSubHeader({ title: 'TOTAL EXPENSES' }));
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                {
                  text: 'Total monthly expenses\n',
                  style: 'label'
                },
                {
                  text: householdsService
                    .getTotalOrGroupTotalExpensesByHousehold({
                      householdExpenses
                    })
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ],
              colSpan: 2
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });

    return content;
  };

  /**
   * Prepare expenses details.
   *
   * @param {Object} expenses
   * @param {Array} contacts
   *
   * @returns {Object}
   */
  const prepareHouseholdDetails = ({ expenses, contacts }) => {
    const content = [];
    const personContacts = _.filter(contacts || [], (c) => !c.isCompany);
    const filteredExpenses = _.filter(expenses, (e) => e?.id && _.isUuid(e?.id));
    if (personContacts?.length && filteredExpenses?.length) {
      const len = filteredExpenses?.length || 0;
      for (let e = 0; e < len; e += 1) {
        const household = filteredExpenses[e];
        content.push(
          helperPdfService.createHeader({
            title: `EXPENSES - HOUSEHOLD OF ${_.map(
              _.filter(personContacts, (c) => household?.idContacts.indexOf(c?.id) > -1),
              (h) => h?.getName().toUpperCase()
            ).join(' AND ')}`,
            pageBreak: 'before'
          })
        );
        const householdGroupExpenses = [
          {
            name: HouseholdGroup.food.title,
            group: _.filter(
              household?.householdExpenses,
              (h) => _.flatten(HouseholdGroup.food.types).indexOf(h?.type?.name) > -1
            )
          },
          {
            name: HouseholdGroup.discretionary.title,
            group: _.filter(
              household?.householdExpenses,
              (h) => _.flatten(HouseholdGroup.discretionary.types).indexOf(h?.type?.name) > -1
            )
          },
          {
            name: HouseholdGroup.children_and_education.title,
            group: _.filter(
              household?.householdExpenses,
              (h) =>
                _.flatten(HouseholdGroup.children_and_education.types).indexOf(h?.type?.name) >
                -1
            )
          },
          {
            name: HouseholdGroup.health_and_wellness.title,
            group: _.filter(
              household?.householdExpenses,
              (h) =>
                _.flatten(HouseholdGroup.health_and_wellness.types).indexOf(h?.type?.name) > -1
            )
          },
          {
            name: HouseholdGroup.transport.title,
            group: _.filter(
              household?.householdExpenses,
              (h) => _.flatten(HouseholdGroup.transport.types).indexOf(h?.type?.name) > -1
            )
          },
          {
            name: HouseholdGroup.property.title,
            group: _.filter(
              household?.householdExpenses,
              (h) => _.flatten(HouseholdGroup.property.types).indexOf(h?.type?.name) > -1
            )
          },
          {
            name: HouseholdGroup.other_commitments.title,
            group: _.filter(
              household?.householdExpenses,
              (h) =>
                _.flatten(HouseholdGroup.other_commitments.types).indexOf(h?.type?.name) > -1
            )
          }
        ];
        content.push(
          prepareExpensesByHousehold({
            householdExpenses: household?.householdExpenses || [],
            householdGroupExpenses
          })
        );
      }
    }
    return content;
  };

  return {
    prepareHouseholdDetails
  };
};
